/**
 * Text Style Utilities
 * 
 * Helper functions for applying text styling (italic, underline, strikethrough)
 * from block configuration to rendered blocks.
 */

export interface TextStyleConfig {
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
}

/**
 * Build CSS text-decoration value from text style configuration
 * Combines underline and strikethrough properties
 * 
 * @param textStyle - Text style configuration object
 * @returns CSS text-decoration value
 */
export function buildTextDecoration(textStyle?: TextStyleConfig): string {
  if (!textStyle) return 'none';
  
  const decorations: string[] = [];
  
  if (textStyle.underline) {
    decorations.push('underline');
  }
  
  if (textStyle.strikethrough) {
    decorations.push('line-through');
  }
  
  return decorations.length > 0 ? decorations.join(' ') : 'none';
}

/**
 * Build CSS font-style value from text style configuration
 * 
 * @param textStyle - Text style configuration object
 * @returns CSS font-style value
 */
export function buildFontStyle(textStyle?: TextStyleConfig): string {
  return textStyle?.italic ? 'italic' : 'normal';
}

/**
 * Build complete text style CSS properties object
 * 
 * @param textStyle - Text style configuration object
 * @returns Object with CSS properties for text styling
 */
export function buildTextStyleProperties(textStyle?: TextStyleConfig): {
  textDecoration: string;
  fontStyle: string;
} {
  return {
    textDecoration: buildTextDecoration(textStyle),
    fontStyle: buildFontStyle(textStyle),
  };
}
